import React, { useMemo } from 'react';
import ScreenActions from '@dyl/components/ScreenActions';
import LinkButton from '@dyl/components/LinkButton';
import { Fab } from '@mui/material';
import { cloneDeep, noop, sample } from 'lodash';
import { Close as CloseIcon, Save as SaveIcon, AutoFixHigh as AutoFixIcon } from '@mui/icons-material';
import useHotkey from '@dyl/hooks/useHotkey';
import useTaskField from "@dyl/pages/Tasks/edit/useTaskField";
import { useAudios } from "@dyl/systems/AudiosManager";
import { useImages } from "@dyl/systems/ImagesManager";

/**
 * Renders save & cancel buttons in the corner of the screen.
 * Also binds Ctrl+S to the save callback.
 */
export default function ScreenSaveCancelBlock({
    disabled = false,

    save = noop,
    saveDisabled = false,

    cancel = noop,
    cancelTo = '',
    cancelDisabled = false,

    showAutofill = false,
}) {

    useHotkey('ctrl+s', () => {
        if (disabled || saveDisabled) return;

        save();
    }, [save, disabled, saveDisabled]);

    return (
        <ScreenActions>
            {showAutofill && <AutofillButton />}

            <LinkButton
                component={Fab}
                variant='extended'
                to={cancelTo}
                size='medium'
                disabled={disabled || cancelDisabled}
                title='Cancel'
            >
                <CloseIcon fontSize='small' />&nbsp;Cancel
            </LinkButton>

            <Fab
                color='primary'
                size='large'
                variant='extended'
                onClick={save}
                disabled={disabled || saveDisabled}
            >
                <SaveIcon />&nbsp;Save
            </Fab>

        </ScreenActions>
    );
}


/*
 On dev allow for an autofill button for fast debugging.
 */
function AutofillButton() {

    const { images } = useImages();
    const { audios } = useAudios();
    const [task, setTask,,taskType] = useTaskField([]);

    const autofill = useMemo(() => {

        const textFields = [
            'task_id_name',
            'label',
            'text',
            'key_text',
            'stimulus_text',
            'stimulus_text_1',
            'stimulus_text_2',
            'question',
            'question_1',
            'question_2',
            'speech_bubble_1',
            'speech_bubble_2',
            'system_question',
            'user_answer_stimulus_text',
            'stimulus',
            ...(['comic_story', 'online_messages', 'spelling'].includes(taskType.id)  ? [] : ['key']),
            'description',
            'poster_title',
            'poster_main_text',
            'note_text',
            'phone_text',
            'email_header_line',
            'email_to_id',
            'email_from_id',
            'email_message'
        ];

        const stringListFields = [
            ...(['comic_story', 'online_messages', 'spelling'].includes(taskType.id) ? ['key'] : []),
        ];

        const imageFields = [
            'image_id',
            'placement_image_id',
            'background_image',
            'background_image_2',
            'background_image_3',
            'stimulus_image',
            'poster_image',
            'poster_picture_image',
            'note_image',
            'phone_image',
            'email_image',
            'decorative_image',
            'decorative_image_2',
            'decorative_image_3',
            'decorative_image_4',

        ];

        const audioFields = [
            'audio_id',
            'final_audio_id',
            'context_audio_id',
        ];

        const randomText = () => Array.from(Array(12), () => Math.floor(Math.random() * 36).toString(36)).join('');
        const randomImage = () => sample(images)?.image_id;
        const randomAudio = () => sample(audios)?.audio_id;

        function checkAndRandomize(key, value) {
            if (textFields.includes(key)) {
                return randomText();
            } else if (stringListFields.includes(key)) {
                return [randomText()];
            } else if (imageFields.includes(key)) {
                return randomImage();
            } else if (audioFields.includes(key)) {
                return randomAudio();
            } else if (key === 'primary_targets') {
                return [`${Math.floor(Math.random() * 100)}`];
            }
            return value;
        }

        // Simple DFS traversal
        function traverse(obj, func) {
            for (let prop in obj) {
                obj[prop] = func(prop, obj[prop]);
                if (typeof obj[prop] == "object" && obj[prop]) {
                    traverse(obj[prop], func);
                }
            }
        }

        return () => {
            const task_ = cloneDeep(task);
            // This code runs in dev only, so keeping logging in.
            console.debug('Before', task_);
            traverse(task_, checkAndRandomize);
            console.debug('After', task_);
            setTask(task_);
        }
    }, [audios, images]);

    return (
        <Fab
            color='secondary'
            size='large'
            variant='extended'
            onClick={autofill}
        >
            <AutoFixIcon />&nbsp;Autofill
        </Fab>
    );
}